import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { signIn, signOut } from 'aws-amplify/auth';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import { logEvent, setUserProperties } from '../utils/analytics';
import PublicNavigation from '../components/PublicNavigation';
import Maintenance from './Maintenance';
import './Auth.css';

const Login = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Check if maintenance mode is enabled (after all hooks are called)
  const maintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === 'true';
  
  if (maintenanceMode) {
    return <Maintenance />;
  }

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { email, password } = formData;

      if (!email || !password) {
        setError('Email and password are required');
        setLoading(false);
        return;
      }

      // Sign out any existing session first
      try {
        await signOut();
      } catch (err) {
        // Ignore if not signed in
      }

      await signIn({
        username: email,
        password,
      });

      // Check if user exists in backend
      const verifyResponse = await authAPI.verify();
      
      if (!verifyResponse.data.userExists) {
        // User needs to complete registration in backend
        // Retrieve name from localStorage (saved during signup)
        let name = '';
        
        try {
          const pendingUserData = localStorage.getItem('pendingUserData');
          if (pendingUserData) {
            const data = JSON.parse(pendingUserData);
            name = data.name || '';
            // Clear localStorage after retrieving
            localStorage.removeItem('pendingUserData');
          }
        } catch (err) {
        }
        
        // Fallback to email prefix if name not found
        if (!name) {
          name = email.split('@')[0];
        }
        
        await authAPI.register({ 
          name: name,
          email: email,
          authProvider: 'email',
        });
      }

      // Call checkAuth without redirect (we'll handle navigation ourselves)
      await checkAuth(false);
      
      // Get user data to set properties
      const userResponse = await authAPI.getMe();
      const userData = userResponse.data;
      
      // Set user properties in GA
      setUserProperties({
        user_name: userData.name || '',
        user_email: userData.email || ''
      });
      
      // Track successful login
      logEvent('login', {
        category: 'user',
        method: 'email'
      });
      
      // Navigate to leagues (questionnaire check removed - level is now per-sport)
      navigate('/leagues');
    } catch (err) {
      if (err.name === 'UserNotConfirmedException') {
        localStorage.setItem('pendingVerificationEmail', formData.email);
        setLoading(false);
        navigate('/signup', {
          state: {
            pendingVerificationEmail: formData.email,
          },
          replace: true,
        });
        return;
      }

      console.error('Login error:', err);
      setError(err.message || 'Failed to log in');
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="auth-page">
      <PublicNavigation />
      <div className="auth-container">
        <div className="auth-card">
          <h1 className="auth-title">Welcome Back</h1>
          <p className="auth-subtitle">Log in to your account</p>

          {error && <div className="error-message">{error}</div>}

          <form onSubmit={handleLogin} className="auth-form">
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                name="email"
                className="form-input"
                value={formData.email}
                onChange={handleChange}
                placeholder="john@example.com"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Password</label>
              <input
                type="password"
                name="password"
                className="form-input"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                required
              />
            </div>

            <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
              {loading ? 'Logging in...' : 'Log In'}
            </button>
          </form>

          <div className="auth-footer">
            <Link to="/forgot-password" style={{ display: 'block', marginBottom: '10px' }}>
              Forgot password?
            </Link>
            Don't have an account? <Link to="/signup">Sign up</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;

